import { Button, Input, Label } from '@up2stay/up2promo-ui';
import { Keyboard } from 'lucide-react';
import { type FormEvent, useId, useState } from 'react';

import { parseManualProductCode } from '@/core/utils/product-link.utils';

export type ProductManualCodeEntryLabels = {
  /** Toggle link, e.g. „Code manuell eingeben“. */
  toggle: string;
  /** Visible field label. */
  label: string;
  placeholder: string;
  submit: string;
  /** Shown when the input is neither a product code nor a sticker link. */
  invalid: string;
};

export interface ProductManualCodeEntryProps {
  labels: ProductManualCodeEntryLabels;
  onSubmit: (result: { hash: string; productType?: string }) => void;
  /** Controlled open state (the scanner's camera error can open it). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
}

/**
 * Typed fallback to the QR scan: the code or the full sticker link, checked
 * like a scanned value. Without it a browser without camera (or with access
 * denied) cannot get past the first setup step (DEV-553).
 */
export function ProductManualCodeEntry({
  labels,
  onSubmit,
  open,
  onOpenChange,
  disabled = false,
}: ProductManualCodeEntryProps) {
  const fieldId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        className="w-fit gap-2 self-start px-2 text-[13.5px] text-muted-foreground hover:text-foreground"
        onClick={() => onOpenChange(true)}
        disabled={disabled}
      >
        <Keyboard className="size-4" aria-hidden />
        {labels.toggle}
      </Button>
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = parseManualProductCode(value);
    if (!result) {
      setError(labels.invalid);
      return;
    }
    setError(null);
    setValue('');
    onOpenChange(false);
    onSubmit(result);
  };

  return (
    <form className="flex flex-col gap-2" onSubmit={handleSubmit} noValidate>
      <Label htmlFor={fieldId}>{labels.label}</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={fieldId}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          placeholder={labels.placeholder}
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          // Enter submits the form; keep focus so a typo can be fixed.
          confirmOnEnter={false}
          autoFocus
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          className="h-10 sm:flex-1"
        />
        <Button
          type="submit"
          className="h-10"
          disabled={disabled || !value.trim()}
        >
          {labels.submit}
        </Button>
      </div>
      {error ? (
        <p id={`${fieldId}-error`} className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </form>
  );
}
